import { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, CreditCard, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiErrorMessage } from '@/lib/documentServices';
import { callbackTicket, createMonerisCheckout, loadMonerisCheckoutScript } from '@/lib/monerisCheckout';

export default function MonerisCheckoutModal({
  open,
  onOpenChange,
  orderId,
  idempotencyKey,
  startCheckout,
  verifyPayment,
  cancelPayment,
  onVerified,
}) {
  const reactId = useId().replace(/:/g, '');
  const containerId = `moneris-checkout-${reactId}`;
  const checkoutRef = useRef(null);
  const activeTicketRef = useRef('');
  const activeRef = useRef(false);
  const startingRef = useRef(false);
  const verificationRef = useRef(false);
  const cancellationRef = useRef(false);
  const closeRequestedRef = useRef(false);
  const [phase, setPhase] = useState('ready');
  const [message, setMessage] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const [retryKey, setRetryKey] = useState(idempotencyKey || '');

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      try { checkoutRef.current?.closeCheckout?.(); } catch { /* provider cleanup only */ }
      checkoutRef.current = null;
      const ticket = activeTicketRef.current;
      if (ticket && !verificationRef.current && !cancellationRef.current && cancelPayment) {
        cancellationRef.current = true;
        Promise.resolve(cancelPayment(orderId, ticket)).catch(() => {});
      }
    };
  }, [cancelPayment, orderId]);

  useEffect(() => {
    if (!open) {
      setPhase('ready');
      setMessage('');
      setCanRetry(false);
      setRetryKey(idempotencyKey || '');
      startingRef.current = false;
      verificationRef.current = false;
      cancellationRef.current = false;
      closeRequestedRef.current = false;
      activeTicketRef.current = '';
    }
  }, [idempotencyKey, open]);

  const finishCancellation = async (ticket, providerError = false) => {
    if (cancellationRef.current || verificationRef.current) return;
    cancellationRef.current = true;
    try { checkoutRef.current?.closeCheckout?.(); } catch { /* provider cleanup only */ }
    setPhase('verifying');
    setMessage('MCC is closing the active payment attempt…');
    try {
      const response = await cancelPayment(orderId, ticket);
      const result = response?.data || response;
      if (!activeRef.current) return;
      activeTicketRef.current = '';
      setCanRetry(result?.can_retry === true);
      setRetryKey(result?.checkout_idempotency_key || result?.idempotency_key || idempotencyKey || '');
      setPhase(providerError ? 'failed' : 'cancelled');
      setMessage(providerError
        ? (result?.safe_message || 'The checkout attempt was closed after a provider error.')
        : 'Checkout was cancelled and the active payment attempt was safely closed.');
      return true;
    } catch (error) {
      if (!activeRef.current) return;
      setCanRetry(false);
      setPhase('uncertain');
      setMessage(apiErrorMessage(error, 'MCC could not confirm that the payment attempt closed. Do not retry until the order status has been checked.'));
      return false;
    } finally {
      cancellationRef.current = false;
    }
  };

  const finishVerification = async (ticket) => {
    if (!ticket || verificationRef.current || cancellationRef.current) return;
    verificationRef.current = true;
    // A provider completion is no longer safely cancellable in the browser.
    // From this point the backend verification response is the only authority.
    activeTicketRef.current = '';
    setPhase('verifying');
    setMessage('MCC is verifying the payment directly with Moneris…');
    try {
      const response = await verifyPayment(orderId, ticket);
      const result = response?.data || response;
      if (!activeRef.current) return;
      const paid = result?.verified === true && result?.payment_status === 'paid';
      setCanRetry(result?.can_retry === true);
      if (paid) {
        setPhase('verified');
        setMessage('Payment approved and verified.');
        try { checkoutRef.current?.closeCheckout?.(); } catch { /* provider cleanup only */ }
        onVerified?.(result);
      } else if (result?.payment_status === 'failed' || result?.payment_status === 'cancelled') {
        setPhase('failed');
        setMessage(result?.safe_message || 'The payment was not approved. No new charge will be attempted automatically.');
      } else {
        setPhase('uncertain');
        setMessage('Payment status is still being reconciled. Do not start another payment until MCC confirms the result.');
      }
    } catch (error) {
      if (!activeRef.current) return;
      setPhase('uncertain');
      setMessage(apiErrorMessage(error, 'MCC could not confirm the payment result. Do not retry until the order status has been checked.'));
    } finally {
      verificationRef.current = false;
    }
  };

  const begin = async () => {
    if (!orderId || startingRef.current || verificationRef.current || cancellationRef.current) return;
    startingRef.current = true;
    setPhase('loading');
    setMessage('Preparing secure checkout…');
    setCanRetry(false);
    let activeTicket = '';
    try {
      const response = await startCheckout(orderId, retryKey || idempotencyKey);
      const preload = response?.data || response;
      const ticket = String(preload?.ticket || '');
      if (!ticket || !preload?.script_url) throw new Error('Secure checkout configuration is incomplete.');
      activeTicket = ticket;
      activeTicketRef.current = ticket;
      if (!activeRef.current) {
        activeTicketRef.current = '';
        try { await cancelPayment(orderId, ticket); } catch { /* no UI remains; backend reconciliation is authoritative */ }
        return;
      }
      if (closeRequestedRef.current) {
        const closed = await finishCancellation(ticket, false);
        closeRequestedRef.current = false;
        if (closed && activeRef.current) onOpenChange(false);
        return;
      }
      await loadMonerisCheckoutScript(preload.script_url);
      if (!activeRef.current || activeTicketRef.current !== ticket || closeRequestedRef.current || cancellationRef.current) return;
      const checkout = createMonerisCheckout({
        environment: preload.environment || preload.mode,
        containerId,
        callbacks: {
          page_loaded: () => {
            if (!activeRef.current) return;
            setPhase('open');
            setMessage('Enter payment details in the secure Moneris window. MCC never receives your card number or security code.');
          },
          cancel_transaction: () => (activeRef.current ? finishCancellation(ticket, false) : undefined),
          error_event: () => (activeRef.current ? finishCancellation(ticket, true) : undefined),
          payment_receipt: () => {
            if (!activeRef.current) return;
            setMessage('Moneris returned a receipt. MCC is waiting for payment completion before verifying it.');
          },
          payment_complete: (payload) => finishVerification(callbackTicket(payload, ticket)),
        },
      });
      checkoutRef.current = checkout;
      setPhase('open');
      setMessage('Secure Moneris checkout is open.');
      checkout.startCheckout(ticket);
    } catch (error) {
      if (!activeRef.current) return;
      if (activeTicket && cancelPayment) {
        await finishCancellation(activeTicket, true);
        return;
      }
      const errorBody = error?.response?.data;
      const errorDetail = errorBody?.detail && typeof errorBody.detail === 'object' ? errorBody.detail : {};
      const retryAllowed = errorBody?.can_retry === true || errorDetail?.can_retry === true;
      const nextRetryKey = errorBody?.checkout_idempotency_key || errorDetail?.checkout_idempotency_key;
      setPhase(retryAllowed ? 'failed' : 'uncertain');
      setCanRetry(retryAllowed);
      if (nextRetryKey) setRetryKey(nextRetryKey);
      setMessage(retryAllowed
        ? apiErrorMessage(error, 'Secure checkout could not be started.')
        : 'MCC could not confirm whether checkout started. Check the order before trying again.');
    } finally {
      startingRef.current = false;
    }
  };

  // The hosted Moneris frame needs real room: while it is mounted the dialog
  // takes over the screen, and outside those phases it stays a compact status
  // card so an error or receipt message is not framed by empty space.
  const expanded = ['loading', 'open', 'verifying'].includes(phase);

  const statusIcon = phase === 'verified'
    ? <CheckCircle2 className="size-5 text-emerald-600" />
    : phase === 'failed' || phase === 'cancelled'
      ? <XCircle className="size-5 text-red-600" />
      : phase === 'uncertain'
        ? <AlertTriangle className="size-5 text-amber-600" />
        : phase === 'loading' || phase === 'verifying'
          ? <Loader2 className="size-5 animate-spin text-[#D4AF37]" />
          : <ShieldCheck className="size-5 text-[#D4AF37]" />;

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (next) { onOpenChange(true); return; }
      if (phase === 'verifying' || cancellationRef.current) return;
      if (startingRef.current && !activeTicketRef.current) {
        closeRequestedRef.current = true;
        setMessage('Waiting for MCC to safely close the payment startup request…');
        return;
      }
      if (activeTicketRef.current && (startingRef.current || ['loading', 'open'].includes(phase))) {
        finishCancellation(activeTicketRef.current, false).then((closed) => {
          if (closed && activeRef.current) onOpenChange(false);
        });
        return;
      }
      try { checkoutRef.current?.closeCheckout?.(); } catch { /* provider cleanup only */ }
      onOpenChange(false);
    }}>
      <DialogContent
        className={expanded
          ? 'flex h-[95vh] max-h-[95vh] w-[96vw] max-w-none flex-col gap-3 overflow-hidden p-4 sm:max-w-[1100px]'
          : 'max-h-[94vh] overflow-y-auto sm:max-w-3xl'}
        overlayClassName={expanded ? 'bg-black/90 backdrop-blur-sm' : undefined}
        data-ph-no-capture="true"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#062B1A]"><CreditCard className="size-5 text-[#D4AF37]" /> Secure payment</DialogTitle>
          <DialogDescription>Payment is collected by Moneris Checkout. MCC does not collect or store raw card details.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] px-4 py-3" aria-live="polite">
          <div className="flex items-start gap-2 text-sm text-[#062B1A]/75">{statusIcon}<span>{message || 'Your order is ready for payment.'}</span></div>
        </div>
        {/* Moneris sizes its own iframe, so the fill rules are forced: without
            them the provider frame keeps a short intrinsic height and scrolls
            the card form inside a sliver of the dialog. */}
        <div
          id={containerId}
          className={expanded
            ? 'min-h-0 flex-1 overflow-auto rounded-lg [&>*]:h-full [&_iframe]:!h-full [&_iframe]:!min-h-[520px] [&_iframe]:!w-full [&_iframe]:!border-0'
            : 'min-h-[80px] overflow-hidden rounded-lg'}
          aria-label="Moneris secure checkout"
        />
        {(phase === 'ready' || canRetry) && phase !== 'verified' && (
          <Button type="button" onClick={begin} className="w-full bg-[#062B1A] text-white hover:bg-[#041B10]">
            <ShieldCheck className="mr-2 size-4" /> {canRetry ? 'Try secure checkout again' : 'Open secure Moneris checkout'}
          </Button>
        )}
        {phase === 'uncertain' && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            For double-charge protection, this screen will not automatically start another charge. Open My Orders and confirm the payment state first.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
